import { PREFERENCE_KEYS, DEFAULT_PREFERENCES } from '../hooks/useUserPreferences';

export interface UserPreferences {
  [key: string]: {
    value: string;
    type: string;
    isSynced: boolean;
    updatedAt: string;
  };
}

// Predefined date format options (date only - time is handled separately)
export const DATE_FORMAT_OPTIONS = {
  'MMM dd, yyyy': 'Jan 15, 2024',
  'MM/dd/yyyy': '01/15/2024',
  'dd/MM/yyyy': '15/01/2024',
  'yyyy-MM-dd': '2024-01-15',
  'dd MMM yyyy': '15 Jan 2024',
  'MMMM dd, yyyy': 'January 15, 2024',
  'dd/MM/yy': '15/01/24',
  'MM/dd/yy': '01/15/24'
} as const;

/**
 * Get timezone display name with GMT offset
 */
export const getTimezoneDisplayName = (timezone: string): string => {
  try {
    const now = new Date();
    const offset = new Intl.DateTimeFormat('en', {
      timeZone: timezone,
      timeZoneName: 'longOffset'
    }).formatToParts(now).find(part => part.type === 'timeZoneName')?.value || '';
    
    return `${timezone} (${offset})`;
  } catch {
    return timezone;
  }
};

/**
 * Get user's timezone preference, falling back to browser timezone
 */
export const getUserTimezone = (preferences?: UserPreferences): string => {
  if (preferences?.[PREFERENCE_KEYS.TIMEZONE]?.value) {
    return preferences[PREFERENCE_KEYS.TIMEZONE].value;
  }
  return Intl.DateTimeFormat().resolvedOptions().timeZone || DEFAULT_PREFERENCES[PREFERENCE_KEYS.TIMEZONE];
};

/**
 * Parse clock24h from one preference row (handles type casing and string storage quirks from API/DB).
 */
export const parseClock24hPreferenceRow = (
  pref: { value: unknown; type?: string } | undefined
): boolean | null => {
  if (!pref) return null;
  const rawType = String(pref.type ?? 'string').toLowerCase().trim();
  const v = pref.value;

  const truthy = (s: string) =>
    s === 'true' || s === '1' || s === 'yes' || s === 'on';
  const falsy = (s: string) =>
    s === 'false' || s === '0' || s === 'no' || s === 'off' || s === '';

  if (rawType === 'boolean') {
    if (typeof v === 'boolean') return v;
    const s = String(v).toLowerCase().trim();
    if (truthy(s)) return true;
    if (falsy(s)) return false;
    return null;
  }

  if (typeof v === 'boolean') return v;
  const s = String(v ?? '').toLowerCase().trim();
  if (truthy(s)) return true;
  if (falsy(s)) return false;
  return null;
};

/**
 * Get user's 24-hour clock preference
 */
export const getUserClock24h = (preferences?: UserPreferences): boolean => {
  const parsed = parseClock24hPreferenceRow(preferences?.[PREFERENCE_KEYS.CLOCK_24H]);
  if (parsed !== null) return parsed;
  return Boolean(DEFAULT_PREFERENCES[PREFERENCE_KEYS.CLOCK_24H]);
};

/**
 * Get user's date format preference
 */
export const getUserDateFormat = (preferences?: UserPreferences): string => {
  if (preferences?.[PREFERENCE_KEYS.DATE_FORMAT]?.value) {
    return preferences[PREFERENCE_KEYS.DATE_FORMAT].value;
  }
  return DEFAULT_PREFERENCES[PREFERENCE_KEYS.DATE_FORMAT];
};

/**
 * Format a date string according to user preferences
 */
export const formatDate = (
  dateString: string, 
  preferences?: UserPreferences,
  customFormat?: string
): string => {
  const date = new Date(dateString);
  const timezone = getUserTimezone(preferences);
  const clock24h = getUserClock24h(preferences);
  const dateFormat = customFormat || getUserDateFormat(preferences);

  // Convert the date to the user's timezone
  const dateInTimezone = new Date(date.toLocaleString("en-US", { timeZone: timezone }));

  // Simple format conversion
  const year = dateInTimezone.getFullYear();
  const month = dateInTimezone.getMonth() + 1;
  const day = dateInTimezone.getDate();
  const hours = dateInTimezone.getHours();
  const minutes = dateInTimezone.getMinutes();

  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const monthName = monthNames[dateInTimezone.getMonth()];

  const fullMonthName = dateInTimezone.toLocaleDateString('en-US', { month: 'long' });
  
  // Format time if the format includes time
  let timeString = '';
  if (dateFormat.includes('HH:mm') || dateFormat.includes('hh:mm')) {
    const displayHours = clock24h ? hours : (hours % 12 || 12);
    const displayMinutes = minutes.toString().padStart(2, '0');
    const ampm = clock24h ? '' : (hours >= 12 ? ' PM' : ' AM');
    timeString = `${displayHours.toString().padStart(2, '0')}:${displayMinutes}${ampm}`;
  }
  
  let formattedDate = dateFormat
    .replace(/yyyy/g, year.toString())
    .replace(/yy/g, year.toString().slice(-2))
    .replace(/MMM/g, monthName)
    .replace(/MMMM/g, fullMonthName)
    .replace(/MM/g, month.toString().padStart(2, '0'))
    .replace(/dd/g, day.toString().padStart(2, '0'))
    .replace(/HH:mm/g, timeString)
    .replace(/hh:mm/g, timeString);

  return formattedDate;
};

/**
 * Format a date string for display in tables (shorter format)
 */
export const formatDateShort = (
  dateString: string, 
  preferences?: UserPreferences
): string => {
  const userDateFormat = getUserDateFormat(preferences);
  return formatDate(dateString, preferences, `${userDateFormat} HH:mm`);
};

/**
 * Format a date string for display in lists (date only)
 */
export const formatDateOnly = (
  dateString: string, 
  preferences?: UserPreferences
): string => {
  const userDateFormat = getUserDateFormat(preferences);
  return formatDate(dateString, preferences, userDateFormat);
};

/**
 * Format a date string for display in lists (long date format)
 */
export const formatDateLong = (
  dateString: string, 
  preferences?: UserPreferences
): string => {
  const userDateFormat = getUserDateFormat(preferences);
  return formatDate(dateString, preferences, userDateFormat);
};

/**
 * Format a date string for display with time only
 */
export const formatTimeOnly = (
  dateString: string, 
  preferences?: UserPreferences
): string => {
  const date = new Date(dateString);
  const timezone = getUserTimezone(preferences);
  const clock24h = getUserClock24h(preferences);
  
  const dateInTimezone = new Date(date.toLocaleString("en-US", { timeZone: timezone }));
  const hours = dateInTimezone.getHours();
  const minutes = dateInTimezone.getMinutes();
  
  const displayHours = clock24h ? hours : (hours % 12 || 12);
  const displayMinutes = minutes.toString().padStart(2, '0');
  const ampm = clock24h ? '' : (hours >= 12 ? ' PM' : ' AM');
  
  return `${displayHours.toString().padStart(2, '0')}:${displayMinutes}${ampm}`;
};

/**
 * Get relative time (e.g., "2 hours ago", "in 3 days")
 */
export const formatRelativeTime = (
  dateString: string, 
  preferences?: UserPreferences
): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
  
  // Use Intl.RelativeTimeFormat if available
  if (typeof Intl !== 'undefined' && Intl.RelativeTimeFormat) {
    const rtf = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });
    
    if (Math.abs(diffInSeconds) < 60) {
      return rtf.format(diffInSeconds, 'second');
    } else if (Math.abs(diffInSeconds) < 3600) {
      return rtf.format(Math.floor(diffInSeconds / 60), 'minute');
    } else if (Math.abs(diffInSeconds) < 86400) {
      return rtf.format(Math.floor(diffInSeconds / 3600), 'hour');
    } else if (Math.abs(diffInSeconds) < 2592000) {
      return rtf.format(Math.floor(diffInSeconds / 86400), 'day');
    } else if (Math.abs(diffInSeconds) < 31536000) {
      return rtf.format(Math.floor(diffInSeconds / 2592000), 'month');
    } else {
      return rtf.format(Math.floor(diffInSeconds / 31536000), 'year');
    }
  }
  
  // Fallback to simple relative time
  if (Math.abs(diffInSeconds) < 60) {
    return diffInSeconds < 0 ? 'in a moment' : 'just now';
  } else if (Math.abs(diffInSeconds) < 3600) {
    const minutes = Math.floor(Math.abs(diffInSeconds) / 60);
    return diffInSeconds < 0 ? `in ${minutes} minute${minutes !== 1 ? 's' : ''}` : `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
  } else if (Math.abs(diffInSeconds) < 86400) {
    const hours = Math.floor(Math.abs(diffInSeconds) / 3600);
    return diffInSeconds < 0 ? `in ${hours} hour${hours !== 1 ? 's' : ''}` : `${hours} hour${hours !== 1 ? 's' : ''} ago`;
  } else {
    const days = Math.floor(Math.abs(diffInSeconds) / 86400);
    return diffInSeconds < 0 ? `in ${days} day${days !== 1 ? 's' : ''}` : `${days} day${days !== 1 ? 's' : ''} ago`;
  }
};

/**
 * Convert UTC datetime string to datetime-local format for user's timezone
 * This is used for datetime-local input fields
 */
export const toDateTimeLocal = (
  utcDateTimeString: string, 
  preferences?: UserPreferences
): string => {
  if (!utcDateTimeString) return '';
  
  const date = new Date(utcDateTimeString);
  const timezone = getUserTimezone(preferences);
  
  // Convert to user's timezone
  const dateInTimezone = new Date(date.toLocaleString("en-US", { timeZone: timezone }));
  
  // Format as datetime-local string (YYYY-MM-DDTHH:mm)
  const year = dateInTimezone.getFullYear();
  const month = (dateInTimezone.getMonth() + 1).toString().padStart(2, '0');
  const day = dateInTimezone.getDate().toString().padStart(2, '0');
  const hours = dateInTimezone.getHours().toString().padStart(2, '0');
  const minutes = dateInTimezone.getMinutes().toString().padStart(2, '0');
  
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

/**
 * Convert datetime-local string from user's timezone to UTC
 * This is used when submitting datetime-local input values
 */
export const fromDateTimeLocal = (
  dateTimeLocalString: string,
  preferences?: UserPreferences
): string => {
  if (!dateTimeLocalString) return '';
  
  const timezone = getUserTimezone(preferences);
  
  // Parse the datetime-local string as if it's in the user's timezone
  const localDate = new Date(dateTimeLocalString);
  
  // Get the timezone offset for the user's timezone
  const utcDate = new Date(localDate.toLocaleString("en-US", { timeZone: "UTC" }));
  const localDateInUserTz = new Date(localDate.toLocaleString("en-US", { timeZone: timezone }));
  
  // Calculate the offset and adjust
  const offset = localDate.getTime() - localDateInUserTz.getTime();
  const utcDateTime = new Date(localDate.getTime() + offset);
  
  return utcDateTime.toISOString();
};